import Box from "@cloudscape-design/components/box"
import Container from "@cloudscape-design/components/container"
import Grid from "@cloudscape-design/components/grid"
import Header from "@cloudscape-design/components/header"
import SpaceBetween from "@cloudscape-design/components/space-between"
import type { DTADocumentContent } from "@/generated/sdk"
import type { DTADetails } from "../../../services/dtaApi"
import CommentForm from "../../../components/Collaboration/CommentForm"
import CommentsSection from "../../../components/Collaboration/CommentsSection"
import DtaAccessPanel from "../../../components/Collaboration/DtaAccessPanel"
import { canProposeAtStage, canVendorCollaborate } from "../../../components/Collaboration/helpers"
import ProposalForm from "../../../components/Collaboration/ProposalForm"
import ProposalsSection from "../../../components/Collaboration/ProposalsSection"
import { getDTADisplayName } from "../../../components/shared/dtaDisplay"

interface EditableSection {
  key: keyof DTADocumentContent
  title: string
}

/**
 * Document_content sections a DAE can edit, mirroring the editor tabs. Comment scopes and proposal
 * targets both use this so neither offers derived fields such as parsed_study_id.
 */
const EDITABLE_SECTIONS: EditableSection[] = [
  { key: "user_guide", title: "User Guide" },
  { key: "transfer_metadata", title: "Data Ingestion Parameters" },
  { key: "transfer_metadata_schema", title: "Metadata" },
  { key: "test_concepts", title: "Test Concepts" },
  { key: "data_quality_rules", title: "Data Quality Rules" },
  { key: "code_lists", title: "Codelists" },
]

const COMMENT_SECTIONS = EDITABLE_SECTIONS.map((section) => section.title)

interface CollaborationPanelProps {
  stream: DTADetails
  dtaId: string
  /**
   * Whether the caller has the internal (sponsor) experience.
   */
  isDAE: boolean
  /**
   * Whether the caller has the external (vendor) experience.
   */
  isVendor: boolean
  /**
   * DTA version being viewed.
   */
  version: string
}

/**
 * Collaboration tab: DTA access (internal experience), comments, and change proposals.
 */
const CollaborationPanel = ({
  stream,
  dtaId,
  isDAE,
  isVendor,
  version,
}: CollaborationPanelProps) => {
  const proposalSections = EDITABLE_SECTIONS.map(({ key, title }) => ({
    id: key,
    title,
    currentValue: stream?.document_content?.[key],
  }))

  // EDC DTAs have no vendor.
  const hasVendor = stream.source_type === "third-party" || stream.source_type === "sdtm"

  // Mirror Cedar: vendors comment at REVIEW/APPROVED/ACTIVE (CommentOnDTA) and propose only at
  // REVIEW (ProposeOnDTA). Internal callers always comment; only vendors propose.
  const vendorCanCollaborate = isVendor && canVendorCollaborate(stream.workflow_stage)
  const showCommentForm = isDAE || vendorCanCollaborate
  const showProposalForm = vendorCanCollaborate && canProposeAtStage(stream.workflow_stage)
  const showCollaborationClosedNote = isVendor && !vendorCanCollaborate

  return (
    <SpaceBetween size="l">
      {isDAE && (
        <DtaAccessPanel
          dtaId={dtaId}
          studyId={stream.study_id}
          dtaName={getDTADisplayName(stream)}
          hasVendor={hasVendor}
        />
      )}
      <Grid
        gridDefinition={[{ colspan: { default: 12, m: 6 } }, { colspan: { default: 12, m: 6 } }]}
      >
        <Container header={<Header variant="h2">Discussions</Header>}>
          <SpaceBetween size="m">
            {showCommentForm && (
              <CommentForm
                dtaId={dtaId}
                vendorCompany={isDAE ? "DAE" : "Vendor"}
                sections={COMMENT_SECTIONS}
                version={version}
                onCommentCreated={() => {
                  const event = new CustomEvent("comments-refresh")
                  window.dispatchEvent(event)
                }}
              />
            )}
            {showCollaborationClosedNote && (
              <Box color="text-body-secondary">
                Collaboration is closed for this DTA at its current stage. You can view existing
                discussions below.
              </Box>
            )}
            <CommentsSection
              dtaId={dtaId}
              isDAE={isDAE}
              onCommentUpdated={() => {}}
              version={version}
            />
          </SpaceBetween>
        </Container>

        <Container header={<Header variant="h2">Change Proposals</Header>}>
          <SpaceBetween size="m">
            {showProposalForm && (
              <ProposalForm
                dtaId={dtaId}
                vendorCompany={stream?.vendor_name || "Vendor"}
                sections={proposalSections}
                version={version}
                onProposalCreated={() => {
                  const event = new CustomEvent("proposals-refresh")
                  window.dispatchEvent(event)
                }}
              />
            )}
            {/* Review controls show only while the DTA's proposals can be reviewed. */}
            <ProposalsSection
              dtaId={dtaId}
              isDAE={isDAE && canProposeAtStage(stream.workflow_stage)}
              onProposalUpdated={() => {}}
              version={version}
            />
          </SpaceBetween>
        </Container>
      </Grid>
    </SpaceBetween>
  )
}

export default CollaborationPanel
